import { BalanceData, SimConstants, StatUse, auraBonusAtLevel, hexTakenAtLevel, levelGains, slowAtLevel } from "@highmarch/domain";

/** A fraction as a short percent: 0.07 → "+7%", 0.0025 → "+0.25%". */
const plusPct = (f: number): string => `+${+(f * 100).toFixed(2)}%`;

/**
 * What a tower's next hub level adds, for the upgrade sheet's "Lv 4 → Lv 5" row, in the stats that tower uses: "+7%
 * damage" on a shooter, "+7% damage & HP" on the Barracks (its soldiers' health climbs too); a damageless tower's own
 * growth (towers.json `levelGrowth`, Kevin 2026-10-24): a Frost tower's slow and its length, a Beacon's aura, a Hex
 * Totem's curse. A tower with neither says what the level always gives: the gear cap.
 */
export function levelGainText(B: BalanceData, towerIndex: number, toLevel: number): string {
  const a = B.towers[towerIndex], from = toLevel - 1;
  const g = levelGains(B, towerIndex), parts: string[] = [];
  if (g.damage === StatUse.Full) {
    const step = B.tables.levelMultAt(toLevel).toNumber() / B.tables.levelMultAt(from).toNumber() - 1;
    parts.push(`${plusPct(step)} damage${g.soldierHp ? " & HP" : ""}`);
  }
  const base = [a.slowFraction, a.slowDurationTicks] as const;
  const [f0, t0] = slowAtLevel(a, from, base), [f1, t1] = slowAtLevel(a, toLevel, base);
  if (f1.gt(f0)) parts.push(`${plusPct(f1.sub(f0).toNumber())} slow`);
  if (t1 > t0) parts.push(`+${+((t1 - t0) / SimConstants.TicksPerSecond).toFixed(2)}s${f1.gt(f0) ? "" : " slow"}`);
  const aura = auraBonusAtLevel(a, toLevel, a.auraDamageBonus).sub(auraBonusAtLevel(a, from, a.auraDamageBonus));
  if (aura.isPositive()) parts.push(`${plusPct(aura.toNumber())} aura`);
  const curse = hexTakenAtLevel(a, toLevel).sub(hexTakenAtLevel(a, from));
  if (curse.isPositive()) parts.push(`${plusPct(curse.toNumber())} curse`);
  return parts.length > 0 ? parts.join(" · ") : `Gear up to Lv ${toLevel}`;
}
